import API from "serviceshift-ui/api-client";

interface JobAssetParams {
  jobId: number;
}

interface DeleteJobAssetParams extends JobAssetParams {
  id: number;
}

interface UploadJobDocumentParams extends JobAssetParams {
  data: FormData;
}

interface UpdateAssetVisibilityParams extends JobAssetParams {
  customer_visible?: boolean;
}

interface UpdateDocumentVisibilityParams extends UpdateAssetVisibilityParams {
  docId: number;
}

interface UpdatePhotoVisibilityParams extends UpdateAssetVisibilityParams {
  photoId: number;
}

type MediaAssetType = "photo" | "video" | "document";

interface FetchCustomerMediaParams {
  customerId: number;
  type: MediaAssetType;
}

interface DeleteCustomerAssetParams {
  customerId: number;
  id: number;
}

interface UpdateCustomerPhotoVisibilityParams {
  customerId: number;
  photoId: number;
  customer_visible?: boolean;
}

interface UpdateCustomerPhotoCaptionParams {
  customerId: number;
  photoId: number;
  caption: string;
}

interface UpdateCustomerDocumentVisibilityParams {
  customerId: number;
  docId: number;
  customer_visible?: boolean;
}

interface BulkUpdateVisibilityParams extends JobAssetParams {
  type: MediaAssetType;
  ids: number[];
  customer_visible: boolean;
}

interface UpdateVideoVisibilityParams extends UpdateAssetVisibilityParams {
  videoId: number;
}

interface UpdatePhotoCaptionParams extends JobAssetParams {
  photoId: number;
  caption: string;
}

interface UpdateVideoCaptionParams extends JobAssetParams {
  videoId: number;
  caption: string;
}

// Captions apply to photos and videos only — documents have no caption column.
interface BulkUpdateCaptionParams extends JobAssetParams {
  type: "photo" | "video";
  ids: number[];
  caption: string;
}

// The bulk caption endpoints are standalone (no job in the URL); each media
// ref carries its own job_id so a request may span jobs.
const BULK_CAPTION_PATHS: Record<"photo" | "video", string> = {
  photo: "/v2/photos/bulk_update_captions",
  video: "/v2/videos/bulk_update_captions"
};

interface InitiateMultipartUploadParams {
  jobId: number;
  extension: string;
  contentType: string;
}

interface InitiateMultipartUploadResponse {
  key: string;
  upload_id: string;
}

interface GetPartUrlsParams {
  key: string;
  uploadId: string;
  partNumbers: number[];
}

interface UploadPart {
  part_number: number;
  url: string;
}

interface CompletedPart {
  part_number: number;
  etag: string;
}

interface CompleteMultipartUploadParams {
  jobId: number;
  key: string;
  uploadId: string;
  parts: CompletedPart[];
  source?: string;
  filename?: string;
}

interface AbortMultipartUploadParams {
  key: string;
  uploadId: string;
}

interface ReservePhotoUploadsParams {
  url: string;
  method: string;
  extensions: string[];
}

interface CompletePhotoUploadParams {
  url: string;
  source: string;
  filename: string;
}

interface ReserveDocumentUploadsParams {
  url: string;
  extensions: string[];
}

interface CompleteDocumentUploadParams {
  url: string;
  filename: string;
}

const BULK_UPDATERS = {
  photo: API.job.bulkUpdatePhotoVisibility,
  video: API.job.bulkUpdateVideoVisibility,
  document: API.job.bulkUpdateDocumentVisibility
};

export default {
  // Read-only rollup fetch for the customer Media & Docs tab; the endpoint path
  // is the pluralized media type. Returns a bare array, so unwrap `res.data`
  // like the other makeRequest actions here.
  fetchCustomerMedia(_: unknown, params: FetchCustomerMediaParams) {
    const { customerId, type } = params;
    return API.makeRequest(`/v2/customers/${customerId}/${type}s`, {
      method: "GET"
    }).then((res) => res.data);
  },

  // Customer-scoped write actions (S4 endpoints). Unlike the job actions these
  // have no api-client wrapper, so they hit makeRequest directly like the
  // customer rollup fetch above.
  deleteCustomerPhoto(_: unknown, params: DeleteCustomerAssetParams) {
    const { customerId, id } = params;
    return API.makeRequest(`/v2/customers/${customerId}/photos/${id}`, {
      method: "DELETE"
    });
  },
  updateCustomerPhotoVisibility(
    _: unknown,
    params: UpdateCustomerPhotoVisibilityParams
  ) {
    const { customerId, photoId, ...visibility } = params;
    return API.makeRequest(`/v2/customers/${customerId}/photos/${photoId}`, {
      method: "PUT",
      data: visibility
    });
  },
  updateCustomerPhotoCaption(
    _: unknown,
    params: UpdateCustomerPhotoCaptionParams
  ) {
    const { customerId, photoId, caption } = params;
    return API.makeRequest(`/v2/customers/${customerId}/photos/${photoId}`, {
      method: "PUT",
      data: { caption }
    });
  },
  deleteCustomerDocument(_: unknown, params: DeleteCustomerAssetParams) {
    const { customerId, id } = params;
    return API.makeRequest(`/v2/customers/${customerId}/documents/${id}`, {
      method: "DELETE"
    });
  },
  updateCustomerDocumentVisibility(
    _: unknown,
    params: UpdateCustomerDocumentVisibilityParams
  ) {
    const { customerId, docId, ...visibility } = params;
    return API.makeRequest(`/v2/customers/${customerId}/documents/${docId}`, {
      method: "PUT",
      data: visibility
    });
  },
  deleteJobPhoto(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deletePhoto(jobId, id);
  },
  deleteJobDocument(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deleteDocument(jobId, id);
  },
  uploadJobDocument(_: unknown, params: UploadJobDocumentParams) {
    const { jobId, data } = params;
    return API.job.uploadDocument(jobId, { body: data });
  },
  updateJobDocumentVisibility(
    _: unknown,
    params: UpdateDocumentVisibilityParams
  ) {
    const { jobId, docId, ...visibility } = params;
    return API.job.updateDocumentVisibility(jobId, docId, visibility);
  },
  updateJobPhotoVisibility(_: unknown, params: UpdatePhotoVisibilityParams) {
    const { jobId, photoId, ...visibility } = params;
    return API.job.updatePhotoVisibility(jobId, photoId, visibility);
  },
  bulkUpdateAssetVisibility(_: unknown, params: BulkUpdateVisibilityParams) {
    const { jobId, type, ids, customer_visible } = params;
    return BULK_UPDATERS[type](jobId, { ids, customer_visible });
  },
  deleteJobVideo(_: unknown, params: DeleteJobAssetParams) {
    const { jobId, id } = params;
    return API.job.deleteVideo(jobId, id);
  },
  updateJobVideoVisibility(_: unknown, params: UpdateVideoVisibilityParams) {
    const { jobId, videoId, ...visibility } = params;
    return API.job.updateVideoVisibility(jobId, videoId, visibility);
  },

  // The single-item update api-client methods forward any payload to the same
  // PUT endpoint, so caption writes reuse them by sending only { caption }.
  updateJobPhotoCaption(_: unknown, params: UpdatePhotoCaptionParams) {
    const { jobId, photoId, caption } = params;
    return API.job.updatePhotoVisibility(jobId, photoId, { caption });
  },
  updateJobVideoCaption(_: unknown, params: UpdateVideoCaptionParams) {
    const { jobId, videoId, caption } = params;
    return API.job.updateVideoVisibility(jobId, videoId, { caption });
  },
  bulkUpdateAssetCaption(_: unknown, params: BulkUpdateCaptionParams) {
    const { jobId, type, ids, caption } = params;
    return API.makeRequest(BULK_CAPTION_PATHS[type], {
      method: "PUT",
      data: { caption, captions: ids.map((id) => ({ id, job_id: jobId })) }
    });
  },

  // Multipart upload actions for large video files
  initiateMultipartUpload(
    _: unknown,
    params: InitiateMultipartUploadParams
  ): Promise<InitiateMultipartUploadResponse> {
    const { jobId, extension, contentType } = params;
    return API.makeRequest(`/v2/jobs/${jobId}/videos/initiate_multipart`, {
      method: "POST",
      data: { extension, content_type: contentType }
    }).then((res) => res.data);
  },

  getUploadPartUrls(
    _: unknown,
    params: GetPartUrlsParams
  ): Promise<{ parts: UploadPart[] }> {
    const { key, uploadId, partNumbers } = params;
    return API.makeRequest("/v2/videos/part_urls", {
      method: "POST",
      data: { key, upload_id: uploadId, part_numbers: partNumbers }
    }).then((res) => res.data);
  },

  completeMultipartUpload(_: unknown, params: CompleteMultipartUploadParams) {
    const { jobId, key, uploadId, parts, source, filename } = params;
    return API.makeRequest(`/v2/jobs/${jobId}/videos/complete_multipart`, {
      method: "POST",
      data: { key, upload_id: uploadId, parts, source, filename }
    }).then((res) => res.data);
  },

  abortMultipartUpload(_: unknown, params: AbortMultipartUploadParams) {
    const { key, uploadId } = params;
    return API.makeRequest("/v2/videos/abort_upload", {
      method: "POST",
      data: { key, upload_id: uploadId }
    });
  },

  // Photo upload actions
  reservePhotoUploads(_: unknown, params: ReservePhotoUploadsParams) {
    const { url, method, extensions } = params;
    return API.makeRequest(url, {
      method,
      data: { extensions }
    }).then((res) => res.data);
  },

  completePhotoUpload(_: unknown, params: CompletePhotoUploadParams) {
    const { url, source, filename } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { source, filename },
      headers: { ignoreError: true }
    });
  },

  // Document upload actions
  reserveDocumentUploads(_: unknown, params: ReserveDocumentUploadsParams) {
    const { url, extensions } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { extensions }
    }).then((res) => res.data);
  },

  completeDocumentUpload(_: unknown, params: CompleteDocumentUploadParams) {
    const { url, filename } = params;
    return API.makeRequest(url, {
      method: "POST",
      data: { filename }
    });
  }
};
